import { v4 as uuidv4 } from 'uuid'
import * as matchAPI from './matches'

/*
Players have name, elo, id
*/

const _yearsBetweenNowAnd = (datetime) => {
  const d = new Date(datetime)
  if (Number.isNaN(d.getTime())) return 0
  const msPerYear = 365.25 * 24 * 60 * 60 * 1000
  const years = Math.floor((Date.now() - d.getTime()) / msPerYear)
  return Math.max(0, years)
}

const _decayFactor = (datetime) => {
  const years = _yearsBetweenNowAnd(datetime)
  return Math.pow(0.95, years)
}

export const getAll = () => {
  const playersData = JSON.parse(localStorage.getItem('players')) || []
  return playersData
}

export const addNew = (name) => {
  const playersData = getAll()
  const newPlayer = {
    name,
    elo: 1200,
    id: uuidv4()
  }
  localStorage.setItem('players', JSON.stringify(playersData.concat(newPlayer)))
  return newPlayer
}

export const updatePlayer = (id, { elo }) => {
  const playersData = getAll()
  const updatedPlayersData = playersData.map((oldPlayer) => {
    return (oldPlayer.id === id) ? {
      ...oldPlayer,
      elo: elo || oldPlayer.elo,
    } : oldPlayer
  })

  localStorage.setItem('players', JSON.stringify(updatedPlayersData))
  return updatedPlayersData.find((player) => player.id === id)
}

export const updatePlayerRankings = (match) => {
  const playersData = getAll()
  const player1 = playersData.find(player => player.id === match.player1.id)
  const player2 = playersData.find(player => player.id === match.player2.id)

  if (!player1 || !player2) {
    console.error('Player not found for match:', match)
    return
  }

  const eloUpdate = (elo1, elo2, outcome, matchDatetime) => {
    const P1 = 1.0 / (1.0 + Math.pow(10, (elo2 - elo1) / 400))
    const K = 40
    const delta = K * (outcome - P1)
    const decay = _decayFactor(matchDatetime)
    return elo1 + delta * decay
  }

  const outcome = parseFloat(match.outcome)
  player1.elo = eloUpdate(player1.elo, player2.elo, outcome, match.datetime)
  player2.elo = eloUpdate(player2.elo, player1.elo, 1 - outcome, match.datetime)

  updatePlayer(player1.id, { elo: player1.elo })
  updatePlayer(player2.id, { elo: player2.elo })
}

export const recomputeRankingsFromStoredMatches = (resetTo = 1200) => {
  // Load players and reset their ELO to a baseline
  const playersData = getAll().map(p => ({ ...p, elo: resetTo }))

  // Load matches (if any) and sort by datetime so we replay them in order
  const matchesData = JSON.parse(localStorage.getItem('matches')) || []
  matchesData.sort((a, b) => new Date(a.datetime) - new Date(b.datetime))

  const eloUpdate = (elo1, elo2, outcome, matchDatetime) => {
    const P1 = 1.0 / (1.0 + Math.pow(10, (elo2 - elo1) / 400))
    const K = 40
    const delta = K * (outcome - P1)
    const decay = _decayFactor(matchDatetime)
    return elo1 + delta * decay
  }

  const getId = (p) => (p && (p.id || p))

  matchesData.forEach((match) => {
    const p1id = getId(match.player1)
    const p2id = getId(match.player2)

    const p1 = playersData.find(p => p.id === p1id)
    const p2 = playersData.find(p => p.id === p2id)

    if (!p1 || !p2) {
      console.warn('Skipping match during recompute - player not found:', match)
      return
    }

    const outcome = parseFloat(match.outcome)

    // Compute both players' new elo based on their current elos (snapshot),
    // so the second player's update doesn't incorrectly use the already-updated value.
    const oldP1 = p1.elo
    const oldP2 = p2.elo
    const newP1 = eloUpdate(oldP1, oldP2, outcome, match.datetime)
    const newP2 = eloUpdate(oldP2, oldP1, 1 - outcome, match.datetime)

    p1.elo = newP1
    p2.elo = newP2
  })

  // Persist updated elos back into stored players (preserve name/id)
  localStorage.setItem('players', JSON.stringify(playersData))
  return playersData
}

/*
  Compute per-match ELO deltas by replaying matches in chronological order
  starting from a baseline ELO for all players (resetTo). Returns a plain object
  keyed by match.id with value { delta1, delta2 } where delta1 is the change
  applied to player1 for that match and delta2 to player2.
*/
export const computeMatchDeltas = (matchesArr, playersArr = null, resetTo = 1200) => {
  const matchesData = Array.isArray(matchesArr) ? matchesArr.slice().sort((a, b) => new Date(a.datetime) - new Date(b.datetime)) : []

  // Initialize players list: prefer provided playersArr, otherwise load stored players.
  const initialPlayers = (playersArr && Array.isArray(playersArr) && playersArr.length > 0)
    ? playersArr.map(p => ({ ...p }))
    : getAll().map(p => ({ ...p }))

  // Map players by id and set baseline elo
  const playersById = new Map(initialPlayers.map(p => [p.id, { ...p, elo: resetTo }]))

  const getId = (p) => (p && (p.id || p))

  const eloUpdate = (elo1, elo2, outcome, matchDatetime) => {
    const P1 = 1.0 / (1.0 + Math.pow(10, (elo2 - elo1) / 400))
    const K = 40
    const delta = K * (outcome - P1)
    const decay = _decayFactor(matchDatetime)
    return elo1 + delta * decay
  }

  const results = {}

  matchesData.forEach((match) => {
    const p1id = getId(match.player1)
    const p2id = getId(match.player2)

    const p1 = playersById.get(p1id)
    const p2 = playersById.get(p2id)

    if (!p1 || !p2) {
      // Can't compute this match if either player is missing
      return
    }

    const outcome = parseFloat(match.outcome)

    const oldP1 = p1.elo
    const oldP2 = p2.elo

    const newP1 = eloUpdate(oldP1, oldP2, outcome, match.datetime)
    const newP2 = eloUpdate(oldP2, oldP1, 1 - outcome, match.datetime)

    const delta1 = newP1 - oldP1
    const delta2 = newP2 - oldP2

    results[match.id] = { delta1, delta2 }

    // advance the players' elo snapshots for subsequent matches
    p1.elo = newP1
    p2.elo = newP2
  })

  return results
}

export const importMatchesAndRecompute = (matches, resetTo = 1200) => {
  // This import implementation performs the import by adding each match one-by-one
  // via the same service calls the UI uses. That simulates "pressing the plus button
  // and entering the result manually" while remaining robust and testable.
  //
  // For each normalized match we:
  //  - ensure players exist in storage
  //  - call matchAPI.addNew(datetime, player1Obj, player2Obj, outcome, comment)
  //  - call updatePlayerRankings(addedMatch) to apply the ELO change immediately
  //
  // Matches are added sequentially so ELO changes happen in chronological order.

  // Ensure matches is an array
  const matchesArr = matches || []

  // Ensure all players referenced by the imported matches exist in storage.
  const existingPlayers = getAll()
  const playersById = new Map(existingPlayers.map(p => [p.id, p]))

  const getId = (p) => (p && (p.id || p))

  // Normalize matches and ensure players exist.
  // This converts diverse input shapes into the canonical match object:
  // { datetime, player1: id, player2: id, outcome: number, comment, id }
  const normalizedMatches = matchesArr.map((match, idx) => {
    const p1raw = match.player1
    const p2raw = match.player2
    const p1id = getId(p1raw)
    const p2id = getId(p2raw)

    if (p1id && !playersById.has(p1id)) {
      playersById.set(p1id, { id: p1id, name: (p1raw && p1raw.name) || `Player ${p1id}`, elo: resetTo })
    }
    if (p2id && !playersById.has(p2id)) {
      playersById.set(p2id, { id: p2id, name: (p2raw && p2raw.name) || `Player ${p2id}`, elo: resetTo })
    }

    // Normalize outcome to a number where 1 = player1 wins, 0 = player2 wins, 0.5 = tie.
    let outcome = match.outcome
    if (typeof outcome === 'string') {
      const o = outcome.trim().toLowerCase()
      if (o === '1' || o === '1.0') outcome = 1
      else if (o === '0' || o === '0.0') outcome = 0
      else if (o === '0.5' || o === '.5' || o === '0,5') outcome = 0.5
      else if (o === 'player1' || o === 'p1' || o === 'win1' || o === 'first') outcome = 1
      else if (o === 'player2' || o === 'p2' || o === 'win2' || o === 'second') outcome = 0
      else outcome = parseFloat(o) || 0.5
    } else if (typeof outcome === 'boolean') {
      outcome = outcome ? 1 : 0
    } else {
      outcome = Number(outcome)
      if (Number.isNaN(outcome)) outcome = 0.5
    }

    const datetime = match.datetime || new Date(Date.now() + idx).toISOString()
    return {
      datetime,
      player1: p1id || getId(p1raw),
      player2: p2id || getId(p2raw),
      outcome,
      comment: match.comment || '',
      id: match.id || uuidv4()
    }
  })

  // Ensure players exist in storage before starting the sequential import.
  localStorage.setItem('players', JSON.stringify(Array.from(playersById.values())))

  // Start with an empty stored matches array so that matchAPI.addNew appends newly
  // imported matches in order (this mirrors manual additions).
  localStorage.setItem('matches', JSON.stringify([]))

  // Add matches one-by-one, using the same service API that the UI uses.
  normalizedMatches.forEach((nm) => {
    const p1 = playersById.get(nm.player1)
    const p2 = playersById.get(nm.player2)

    if (!p1 || !p2) {
      console.warn('Skipping match during import - player not found:', nm)
      return
    }

    // matchAPI.addNew expects player objects (as used by the UI form).
    const addedMatch = matchAPI.addNew(nm.datetime, p1, p2, nm.outcome, nm.comment)

    // Reuse the existing updatePlayerRankings logic to apply ELO changes the same way
    // the UI would when a match is added manually.
    try {
      updatePlayerRankings(addedMatch)
    } catch (err) {
      console.error('Failed to update player rankings for imported match:', addedMatch, err)
    }
  })

  // Return the current players snapshot after the sequential import/updates.
  return getAll()
}

export const clearAll = () => {
  localStorage.removeItem('players')
}
